import * as ReactRouter from 'react-router-dom'
import * as ReactRedux from 'react-redux'
import React from 'react'
import querystring from 'querystring'

import * as Analytics from '@rushplay/analytics'
import * as Api from '@rushplay/api-client'
import * as Herz from '@rushplay/herz'

import * as CombinedSelectors from './combined-selectors'
import * as Player from './player'
import * as Promotions from './promotions'
import * as Session from './session'
import * as ServerConfiguration from './server-configuration'
import FluidInitialised from './fluid/fluid-initialised'
import { useFluid } from './fluid/fluid-provider'
import { usePlayerLimits } from './use-player-limits.js'
import { useSafeUpdateQuery } from './use-safe-update-query'

// TODO: check if we still need to have redeem link,
// if not, change redeem to withdraw and remove this function
function getTransactiontype(transaction) {
  if (transaction === 'redeem') {
    return 'withdrawal'
  }

  return transaction || 'deposit'
}

export function FluidWrapper() {
  const fingerprint = Herz.Seon.useFingerprint()
  const authenticated = ReactRedux.useSelector(state =>
    Session.isAuthenticated(state.session)
  )

  const withdrawalInfo = ReactRedux.useSelector(state =>
    Player.getWithdrawalInformation(state.player)
  )

  const bonusBalanceCents = withdrawalInfo.bonusBalanceCents
  const moneyBalanceCents = withdrawalInfo.moneyBalanceCents
  const wagerBalanceCents = withdrawalInfo.wagerBalanceCents

  const translate = Herz.I18n.useTranslate(
    () => [
      'wallet.withdrawl.bonus-disclaimer.title',
      'wallet.withdrawl.bonus-disclaimer.message',
    ],
    []
  )

  const withdrawalWarning = React.useMemo(
    () =>
      JSON.stringify({
        title: translate('wallet.withdrawl.bonus-disclaimer.title'),
        message: translate('wallet.withdrawl.bonus-disclaimer.message', {
          bonusAmount: bonusBalanceCents,
          moneyAmount: moneyBalanceCents,
          wagerBalance: wagerBalanceCents,
        }),
      }),
    [translate, bonusBalanceCents, moneyBalanceCents, wagerBalanceCents]
  )

  const configuration = ServerConfiguration.useContext()

  const { status, config } = useFluid()

  const depositMethodRef = React.useRef(null)

  const withdrawableBalanceCents = ReactRedux.useSelector(state =>
    Player.getWithdrawableBalanceCents(state.player)
  )
  const balanceCents = ReactRedux.useSelector(state =>
    Player.getBalanceCents(state.player)
  )

  const withdrawableBalance = withdrawableBalanceCents
    ? withdrawableBalanceCents / 100
    : 0
  const balance = balanceCents ? balanceCents / 100 : 0

  const dispatch = ReactRedux.useDispatch()
  const location = ReactRouter.useLocation()
  const closeQuery = useSafeUpdateQuery({
    ['wallet']: null,
  })
  const history = ReactRouter.useHistory()

  const query = querystring.parse(location.search?.substring(1))

  const transactionType = getTransactiontype(query.wallet)

  const data = usePlayerLimits(query.wallet)

  const depositCampaigns = ReactRedux.useSelector(state =>
    Promotions.getDepositCampaigns(state.promotions, { dateNow: Date.now() })
  )

  const depositOffers = ReactRedux.useSelector(state =>
    CombinedSelectors.getCalculatedOffers(state, { amountCents: 0 })
  )

  const campaignKeyToId = React.useMemo(() => {
    const map = {}
    Object.values(depositCampaigns).forEach(promotion => {
      const key = promotion?.key
      const id = promotion?.campaign?.id
      if (key && id) {
        map[key.toString()] = id
      }
    })
    return map
  }, [depositCampaigns])

  const depositOfferIds = React.useMemo(
    () =>
      new Set(
        (depositOffers || [])
          .map(offer => offer?.id?.toString())
          .filter(Boolean)
      ),
    [depositOffers]
  )

  React.useEffect(() => {
    if (authenticated) {
      dispatch(Player.fetchWithdrawInformation())
    }
  }, [authenticated])

  function onCommand(event) {
    // eslint-disable-next-line no-console
    console.info(`%cFluid COMMAND: ${event.detail}`, 'color: lightgreen', event)

    if (event.detail.message === 'close') {
      onClose()
    }
  }

  /**
   * Handles informational events emitted by the Fluid Payments widget.
   *
   * @param {CustomEvent} event - The custom event emitted by the Fluid widget.
   *
   * @see {@link https://docs.fluidpayments.io/docs/integration-manual/fluid-widget-integration} Official Integration Manual
   *
   * Possible `event.detail` values include:
   * - `"initialised"` - Wallet is ready to open; opening should be blocked before this event.
   * - `"opened"` - Wallet has been opened.
   * - `"destroyed"` - Wallet instance has been terminated.
   * - `"internal-operation-change"` - Transaction flow updated internally (e.g., withdrawal without a positive balance).
   * - `"success-cta-click"` - User clicked the CTA on a successful deposit/withdrawal screen.
   * - `"deposit-success"` - Deposit completed successfully.
   * - `"deposit-failure"` - Deposit attempt failed.
   * - `"withdrawal-success"` - Withdrawal completed successfully.
   * - `"withdrawal-failure"` - Withdrawal attempt failed.
   * - `"withdrawal-cancelled"` - Withdrawal was cancelled by the user.
   * - `"start-kyc-verification"` - User clicked "Verify" in the fluent message.
   * - `"contact-support"` - User clicked "Contact Support" on the failure screen.
   * - `"bonus-changed"` - User changed selected deposit bonus (or deselected it).
   */
  function onInfo(event) {
    const detail = event.detail || {}
    const message = detail.message || detail

    // eslint-disable-next-line no-console
    console.info(`%cFluid INFO: ${message}`, 'color: cornflowerblue', detail)

    switch (message) {
      case 'close':
        onClose()
        break
      case 'opened':
        break
      case 'payment-method-changed':
        depositMethodRef.current = detail.selectedMethodName || null
        dispatch(Analytics.selectPaymentMethod(depositMethodRef.current))
        break
      case 'bonus-changed': {
        const previous = detail.previousBonusCode
        const selected = detail.selectedBonusCode

        if (previous) {
          const previousCampaignId = campaignKeyToId[previous]
          const previousOptInState =
            depositCampaigns[previous]?.campaign?.optInState
          if (previousCampaignId && previousOptInState === 'in') {
            dispatch(
              Api.optOutCampaign(previousCampaignId, {
                success: () => Promotions.optOut(previous),
                version: 2,
              })
            )
          } else if (depositOfferIds.has(previous)) {
            dispatch(Api.deleteClaimedDepositOffer({ version: 2 }))
          }
        }

        if (selected) {
          const selectedCampaignId = campaignKeyToId[selected]
          const selectedOptInState =
            depositCampaigns[selected]?.campaign?.optInState
          if (selectedCampaignId && selectedOptInState === 'out') {
            dispatch(
              Api.optInCampaign(selectedCampaignId, {
                success: () => Promotions.optIn(selected),
                version: 2,
              })
            )
          } else if (depositOfferIds.has(selected)) {
            dispatch(Api.claimDepositOffer(selected, { version: 2 }))
          }
        }
        break
      }
      case 'success-cta-click':
        history.push('/casino')
        break
      case 'withdrawal-success':
        dispatch(
          Player.fetchPlayerInfo({
            success: () =>
              Analytics.completeTransaction('successful', transactionType),
          })
        )
        break
      case 'deposit-success': {
        const depositMethod = depositMethodRef.current
        dispatch(Api.deleteClaimedDepositOffer({ version: 2 }))
        // Delay to allow the backend to update deposit count before fetching,
        // so that deposit.count is accurate for FTD detection in GTM
        setTimeout(() => {
          dispatch(
            Player.fetchPlayerInfo({
              success: () =>
                Analytics.completeTransaction('successful', depositMethod),
            })
          )
        }, 5000)
        break
      }
      case 'withdrawal-failure':
      case 'deposit-failure':
        dispatch(
          Analytics.completeTransaction('failed', depositMethodRef.current)
        )

        break
      case 'withdrawal-cancelled':
        dispatch(
          Analytics.completeTransaction('cancelled', depositMethodRef.current)
        )

        break
      case 'contact-support':
        window.location.href = `mailto:${configuration?.supportEmail ||
          'stupport@starsplay.com'}?subject=Support Request&body=Please describe your issue...`
        break
      default:
        break
    }
  }

  /**
   * Handles error events emitted by the Fluid Payments widget.
   *
   * @param {CustomEvent} event - The custom event containing error details from the Fluid widget.
   *
   * @see {@link https://docs.fluidpayments.io/docs/integration-manual/fluid-widget-integration} Official Integration Manual
   *
   * Possible `event.detail` values include:
   * - `"initialisation-failure"` - Fluid failed to configure, possibly due to network or backend issues.
   * - `"operation-execution-failure"` - An operation failed unexpectedly due to an internal error.
   * - `"invalid-bonus-data"` - Bonus data is not in the expected format.
   * - `"invalid-user-data"` - User data is not in the expected format.
   * - `"invalid-transaction-attributes"` - Transaction attributes are malformed or incorrect.
   */
  function onError(event) {
    // eslint-disable-next-line no-console
    console.error(`Fluid ERROR: ${event.detail}`, event)
  }

  function onClose() {
    history.push(`?${closeQuery}`)
  }

  if (status !== 'ready' || !authenticated || fingerprint.fetching) {
    return null
  }

  return (
    <FluidInitialised
      open={Boolean(query.wallet)}
      transaction={transactionType}
      onCommand={onCommand}
      onInfo={onInfo}
      onError={onError}
      balance={balance}
      withdrawableBalance={withdrawableBalance}
      depositLimit={Math.min(data.limits.max, data.playerLimitMaximum)}
      onClose={onClose}
      bonuses={JSON.stringify(config.bonuses)}
      seonSession={fingerprint?.value}
      withdrawalWarning={withdrawalWarning}
    />
  )
}
